import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Injector, NgModuleRef, ViewContainerRef, createNgModule } from '@angular/core';

import { TimestampService } from '@core/services/timestamp.service';
import { LazyResult } from '@lazy-load-legacy.model';

@Injectable()
export class LazyLoadService {
    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly timestampService: TimestampService,
        private readonly injector: Injector,
    ) { }

    public loadScript(path: string): Promise<LazyResult> {
        return new Promise((resolve) => {
            const node = this.document.createElement('script');
            node.type = 'text/javascript';
            node.src = `${path}?t=${this.timestampService.getTs()}`;

            node.onload = () => {
                resolve({
                    path,
                    loaded: true,
                    status: 'ok',
                });
            };

            node.onerror = (error: any) => {
                resolve({
                    path,
                    loaded: false,
                    status: 'error',
                    error,
                });
            };

            this.document.getElementsByTagName('body')[0].appendChild(node);
        });
    }

    public loadStyle(path: string, rel: string = 'stylesheet', waitForOnLoad = false): Promise<LazyResult> {
        return new Promise((resolve) => {
            const node = this.document.createElement('link');
            node.rel = rel;
            node.type = 'text/css';
            node.href = `${path}?t=${this.timestampService.getTs()}`;

            this.document.getElementsByTagName('head')[0].appendChild(node);

            if (!waitForOnLoad) {
                const item: LazyResult = {
                    path,
                    loaded: true,
                    status: 'ok',
                };

                resolve(item); return;
            }
            node.onload = () => {
                resolve({
                    path,
                    loaded: true,
                    status: 'ok',
                });
            };

            node.onerror = (error: any) => {
                resolve({
                    path,
                    loaded: false,
                    status: 'error',
                    error,
                });
            };
        });
    }

    public async loadDynamicModuleAndComponent(
        importPromise: Promise<any>,
        classes: { moduleName: string; componentReference: any; initializeFunction?: any },
        componentContainer: ViewContainerRef,
        componentData: Record<string, any> = {}
    ): Promise<void> {
        // Loading of the module
        const _module = await importPromise;
        const moduleRef: NgModuleRef<unknown> = createNgModule(_module[classes.moduleName], this.injector);
        // Destroying already existing views inside the container
        componentContainer.clear();
        // Creation and injection of the component into the container
        const component = componentContainer.createComponent(classes.componentReference, { ngModuleRef: moduleRef });
        // Input data providing and component initializing
        Object.entries(componentData).forEach(([paramName, paramValue]) => {
            component.instance[paramName] = paramValue;
        });
        if (classes?.initializeFunction) {
            component.instance[classes.initializeFunction]();
        }
        component.changeDetectorRef.detectChanges();
    }

    public loadDynamicComponent(
        classes: { componentReference: any; initializeFunction?: any },
        componentContainer: ViewContainerRef,
        componentData: Record<string, any>
    ): void {
        // Destroying already existing views inside the container
        componentContainer.clear();
        // Creation and injection of the component into the container
        const component = componentContainer.createComponent(classes.componentReference);
        // Input data providing and component initializing
        Object.entries(componentData).forEach(([paramName, paramValue]) => {
            component.instance[paramName] = paramValue;
        });
        if (classes?.initializeFunction) {
            component.instance[classes.initializeFunction]();
        }
        component.changeDetectorRef.detectChanges();
    }
}
